import React, { useMemo, useRef, useEffect, useImperativeHandle } from "react";
import CustomUploader from "./CustomUploader";
import FileUploadField from "./FileUploadField";
import { FixedCropper, ImageRestriction } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";

const WhiteTrashIcon = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
        fill="white"
      />
    </svg>
  );
};

const FileUploader = ({
  limitMegabyteSize,
  fileList,
  onChange,
  showUploadList,
  enableCropper = false,
  onCrop,
  cropperRef,
  ...restProps
}) => {
  const internalCropperRef = useRef(null);
  const activeCropperRef = cropperRef || internalCropperRef;
  const uploadedFile = fileList && fileList.length > 0 ? fileList[0] : null;

  const getImageUrl = (file) => {
    if (!file) return null;
    if (typeof file === "string") return file;
    if (file.url) return file.url;
    if (file.thumbUrl) return file.thumbUrl;
    if (file.originFileObj) return URL.createObjectURL(file.originFileObj);
    return null;
  };

  const imageUrl = useMemo(() => getImageUrl(uploadedFile), [uploadedFile]);

  const handleRemove = () => {
    if (onChange) {
      onChange({ fileList: [] });
    }
  };

  // Function to get cropped image data URL
  const getCroppedImage = () => {
    if (activeCropperRef.current) {
      // Check if it's the actual cropper instance
      if (typeof activeCropperRef.current.getCanvas === "function") {
        const canvas = activeCropperRef.current.getCanvas();
        if (canvas) {
          return canvas.toDataURL("image/jpeg", 0.9);
        }
      }
    }
    return null;
  };

  // Expose the getCroppedImage function through the ref
  // Must be called unconditionally (React hooks rule)
  useImperativeHandle(
    cropperRef,
    () => ({
      getCroppedImage,
    }),
    [],
  );

  // Hide upload list when there's an uploaded file
  const shouldShowUploadList = uploadedFile ? false : showUploadList !== false;

  return (
    <div>
      {!uploadedFile || (uploadedFile && !enableCropper) ? (
        <CustomUploader
          {...restProps}
          fileList={fileList}
          onChange={onChange}
          showUploadList={shouldShowUploadList}
        >
          <FileUploadField
            limitMegabyteSize={limitMegabyteSize}
            uploadedFile={uploadedFile}
            onRemove={uploadedFile ? handleRemove : null}
            enableCropper={enableCropper}
            onCrop={onCrop}
          />
        </CustomUploader>
      ) : (
        <div style={{ width: "100%", height: "306px", position: "relative" }}>
          <FixedCropper
            ref={activeCropperRef}
            src={imageUrl}
            stencilProps={{
              handlers: false,
              lines: false,
              movable: false,
              resizable: false,
            }}
            stencilSize={{
              width: 544, // 16:9 aspect ratio (300 * 16/9 = 533.33)
              height: 306, // 16:9 aspect ratio
            }}
            imageRestriction={ImageRestriction.stencil}
            backgroundWrapperProps={{
              scaleImage: true,
              moveImage: true,
            }}
            style={{
              width: "100%",
              height: "306px",
            }}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRemove();
            }}
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              background: "rgba(0, 0, 0, 0.7)",
              border: "none",
              cursor: "pointer",
              padding: "6px",
              zIndex: 1,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
            }}
          >
            <WhiteTrashIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
